// src/components/restaurant/menu/MenuVariantFormEdit.jsx
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";

import { FaTrashAlt } from "react-icons/fa";
import { empty } from "../../../helper/generalHelper";
import ImageUploadPreview from "../../../components/restaurant/ImageUploadPreview";

// More unique SKU generation
const generateSKU = () => {
  const timestamp = Date.now();
  const random = Math.floor(1000 + Math.random() * 9000);
  return `SKU-${timestamp}-${random}`;
};

const supportedLanguages = ["en", "hi"];

const MenuVariantFormEditStaff = forwardRef(
  ({ inventory, categoryId, onDelete, index, menuData, isEdit }, ref) => {
    const [menu, setMenu] = useState({
      id: null,
      sku: "",
      mrp: "",
      price: "",
      quantity: "",
      hasTax: 0,
      tax: "",
      inventory_manage: 0,
      ingredients: [],
      images: [],
      uploads: [],
      activeLang: "en",
      translations: supportedLanguages.reduce((acc, lang) => {
        acc[lang] = { title: "", shortDescription: "", description: "" };
        return acc;
      }, {}),
      has_variation: 0,
    });

    const [uploads, setUploads] = useState([]);

    // Transform API data to match component structure
    const transformVariantData = (data) => {
      if (!data) return null;

      return {
        id: data.id,
        sku: data.sku,
        mrp: data.mrp,
        price: data.price,
        quantity: data.quantity,
        hasTax: (Number(data.has_tax) === 1 && parseFloat(data.tax) > 0) ? 1 : 0,
        tax: data.tax,
        inventory_manage: data.inventory_manage,
        ingredients: data.ingredients || [],
        images: data.images || [],
        uploads: data.uploads || [],
        translations: data.translations || {},
        has_variation: data.has_variation || 0,
      };
    };

    // Prefill on edit
    useEffect(() => {
      if (!menuData) {
        // Reset for new variant with generated SKU
        setMenu({
          id: null,
          sku: generateSKU(),
          mrp: "",
          price: "",
          quantity: "",
          hasTax: 0,
          tax: "",
          inventory_manage: 0,
          ingredients: [],
          images: [],
          uploads: [],
          activeLang: "en",
          translations: supportedLanguages.reduce((acc, lang) => {
            acc[lang] = { title: "", shortDescription: "", description: "" };
            return acc;
          }, {}),
          has_variation: 0,
        });
        return;
      }

      console.log("Original variant data:", menuData);

      const transformedData = transformVariantData(menuData);
      console.log("Transformed variant data:", transformedData);

      if (transformedData) {
        setMenu((prev) => ({
          ...prev,
          id: transformedData.id || null,
          sku: transformedData.sku || generateSKU(),
          mrp: transformedData.mrp || "",
          price: transformedData.price || "",
          quantity: transformedData.quantity || "",
          hasTax: transformedData.hasTax ?? 0,
          tax: transformedData.tax || "",
          inventory_manage: transformedData.inventory_manage ?? 0,
          ingredients: transformedData.ingredients || [],
          images: transformedData.images || [],
          uploads: transformedData.uploads || [],
          translations: supportedLanguages.reduce((acc, lang) => {
            acc[lang] = {
              title: transformedData.translations?.[lang]?.title || "",
              shortDescription:
                transformedData.translations?.[lang]?.shortDescription || "",
              description:
                transformedData.translations?.[lang]?.description || "",
            };
            return acc;
          }, {}),
          has_variation: 0,
          activeLang: "en",
        }));
      }
    }, [menuData]);

    const updateField = (field, value) =>
      setMenu((prev) => ({ ...prev, [field]: value }));

    const updateTranslation = (lang, field, value) => {
      setMenu((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [field]: value,
          },
        },
      }));
    };

    const toggleField = (field) =>
      setMenu((prev) => ({ ...prev, [field]: prev[field] === 1 ? 0 : 1 }));

    const addIngredient = () =>
      setMenu((prev) => ({
        ...prev,
        ingredients: [
          ...prev.ingredients,
          { id: null, inventoryId: "", measurement: "", reduceStock: "" },
        ],
      }));

    const updateIngredient = (idx, field, value) => {
      const updated = [...menu.ingredients];
      updated[idx][field] = value;
      setMenu((prev) => ({ ...prev, ingredients: updated }));
    };

    const removeIngredient = (idx) =>
      setMenu((prev) => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== idx),
      }));

    useImperativeHandle(ref, () => ({
      getSanitizedData: () => {
        const sanitizedData = {
          ...menu,
          categoryId: Number(categoryId),
          sku: menu.sku || generateSKU(),
          mrp: menu.mrp !== "" ? parseFloat(menu.mrp) : 0,
          price: menu.price !== "" ? parseFloat(menu.price) : 0,
          quantity: menu.quantity !== "" ? parseInt(menu.quantity) : 0,
          hasTax: Number(menu.hasTax),
          tax: menu.hasTax ? (menu.tax !== "" ? parseFloat(menu.tax) : 0) : 0,
          inventory_manage: Number(menu.inventory_manage),
          ingredients:
            menu.inventory_manage === 1
              ? menu.ingredients
                  .filter((ing) => ing.inventoryId && ing.inventoryId !== "")
                  .map((ing) => ({
                    id: ing.id || null,
                    inventoryId: ing.inventoryId,
                    measurement: empty(ing.measurement) ? null : ing.measurement,
                    reduceStock:
                      ing.reduceStock !== "" ? parseFloat(ing.reduceStock) : 0,
                  }))
              : [],
          translations: menu.translations,
          images: menu.images, // This should be array of image filenames, not File objects
          uploads: menu.uploads,
          has_variation: 0,
        };

        // Only include id if it exists (for edits)
        if (menu.id) {
          sanitizedData.id = menu.id;
        }

        console.log(`Variant ${index} sanitized data:`, sanitizedData);
        return sanitizedData;
      },
      getUploads: () => uploads, // This returns the File objects for upload
      regenerateSKU: () => {
        updateField("sku", generateSKU());
      },
    }));

    return (
      <div className="relative p-6 mb-8 border rounded-lg bg-white shadow-lg">
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="absolute top-2 right-2 text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
            title="Remove this Variant"
          >
            <FaTrashAlt size={16} />
          </button>
        )}

        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Variant {index + 1}
          {menu.id && (
            <span className="text-sm text-gray-600 ml-2">(ID: {menu.id})</span>
          )}
        </h3>

        {/* Translations */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-700 mb-3">
            Variant Details
          </h4>
          <div className="flex gap-2 mb-3">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() =>
                  setMenu((prev) => ({ ...prev, activeLang: lang }))
                }
                className={`px-4 py-1 rounded text-sm border transition-colors ${
                  menu.activeLang === lang
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

                  {[{ label: "Item Name", field: "title" },{ label:"shortDescription", field: "shortDescription" },{ label:"description", field: "description" }].map(({field, label }) => {
            const Component = field === "description" ? "textarea" : "input";
                 label =
                  label === "shortDescription"
                    ? "Short Description"
                    : label.charAt(0).toUpperCase() + label.slice(1);
            return (
              <div key={label} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                  {label} ({menu.activeLang})
                </label>
                <Component
                  placeholder={`Enter ${field} in ${menu.activeLang}`}
                  value={menu.translations[menu.activeLang][field]}
                  onChange={(e) =>
                    updateTranslation(menu.activeLang, field, e.target.value)
                  }
                  className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={field === "description" ? 4 : undefined}
                />
              </div>
            );
          })}
        </div>

        {/* Basic Info */}
        <div className="mb-8">
          {/* <h4 className="text-md font-semibold text-gray-700 mb-4">
            Basic Info
          </h4> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col justify-center h-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU *
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="text"
                  value={menu.sku}
                  onChange={(e) => updateField("sku", e.target.value)}
                  className="flex-grow border px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Auto-generated SKU"
                  required
                />
                <button
                  type="button"
                  onClick={() => updateField("sku", generateSKU())}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                >
                  Generate
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                SKU must be unique across all menu items
              </p>
            </div>
          </div>
        </div>

        {/* Pricing & Inventory */}
        <div className="mb-8">
          <h4 className="text-md font-semibold text-gray-700 mb-4">
            Pricing & Inventory
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {[
              { field: "mrp", label: "MRP" },
              { field: "price", label: "Selling Price" },
              { field: "quantity", label: "Quantity" },
            ].map(({ field, label }) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={menu[field]}
                  onChange={(e) => updateField(field, e.target.value)}
                  className="w-full border px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Enter ${label.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Include Tax Toggle */}
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-gray-700 font-medium">
                Include Tax
              </span>
              <button
                type="button"
                onClick={() => toggleField("hasTax")}
                className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                  menu.hasTax ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                    menu.hasTax ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Inventory Manage */}
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-gray-700 font-medium">
                Inventory Management
              </span>
              <button
                type="button"
                onClick={() => toggleField("inventory_manage")}
                className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${
                  menu.inventory_manage ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-5 h-5 transform bg-white rounded-full shadow transition-transform ${
                    menu.inventory_manage ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {menu.hasTax === 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax Percentage
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={menu.tax}
                onChange={(e) => updateField("tax", e.target.value)}
                className=" border px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter tax percentage"
              />
            </div>
          )}
        </div>

        {/* Ingredients */}
        {menu.inventory_manage === 1 && (
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-700 mb-3">
              Ingredients
            </h4>
            {menu.ingredients.length === 0 && (
              <p className="text-sm text-gray-500 mb-4">
                No ingredients added yet.
              </p>
            )}
            {menu.ingredients.length > 0 && (
              <div className="hidden sm:grid grid-cols-4 gap-4 px-2 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded-t-md">
                <div>Ingredient</div>
                <div>Measurement</div>
                <div>Reduce Stock</div>
                <div>Action</div>
              </div>
            )}
            {menu.ingredients.map((ing, i) => (
              <div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center py-3 border-b"
              >
                <select
                  value={ing.inventoryId || ""}
                  onChange={(e) => updateIngredient(i, "inventoryId", e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Ingredient</option>
                  {inventory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.itemName} ({item.measurement})
                    </option>
                  ))}
                </select>
                <select
                  value={ing.measurement}
                  onChange={(e) =>
                    updateIngredient(i, "measurement", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Measurement</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="piece">piece</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Reduce Stock"
                  value={ing.reduceStock}
                  onChange={(e) =>
                    updateIngredient(i, "reduceStock", e.target.value)
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => removeIngredient(i)}
                  className="text-red-600 hover:text-red-800 flex justify-start sm:justify-center p-1 rounded hover:bg-red-50 transition-colors"
                  title="Remove Ingredient"
                >
                  <FaTrashAlt size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addIngredient}
              className="mt-3 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm rounded transition-colors"
            >
              + Add Ingredient
            </button>
          </div>
        )}

        {/* Variant Images */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-700 mb-3">
            Variant Images
          </h4>
          <ImageUploadPreview
            initialImages={menu.uploads}
            onChange={(imgs) => setMenu((prev) => ({ ...prev, images: imgs }))}
            onUploads={setUploads}
          />
        </div>

      </div>
    );
  }
);

MenuVariantFormEditStaff.displayName = "MenuVariantFormEditStaff";

export default MenuVariantFormEditStaff;
